import { useMemo, useState } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxmUserauthV1OrganizationQueryServiceOrganizationGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-organization-query-service-organization-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { Box, DEFAULT_BOX_PADDING } from '@interxfi/design-system/components/box/box';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { Tab } from '@interxfi/design-system/components/tabs/shared';
import { Tabs } from '@interxfi/design-system/components/tabs/tabs';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1OrganizationState } from '@interxfi/types';
import { UserauthOrganizationProgramsList } from '../../../../../../../features/userauth/organization/program/userauth-organization-programs-list';
import { UserauthOrganizationEntityMenu } from '../../../../../../../features/userauth/organization/userauth-organization-entity-menu';
import { getUserauthOrganizationStateListItems } from '../../../../../../../features/userauth/organization/userauth-organization-list-items';
import { UserauthOrganizationStatusPill } from '../../../../../../../features/userauth/organization/userauth-organization-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';

export const Route = createFileRoute('/_app/_platform/_context-required/organization/_organization/$organizationId/_detail')({
  component: OrganizationDetailLayout,
});

type DetailOverviewTab = 'details' | 'controls';

function getListItems(data: IxmUserauthV1OrganizationState | undefined) {
  const items = getUserauthOrganizationStateListItems(data);

  return [items.data.profile.name, items.metadata.createdAt];
}

function getControlListItems(data: IxmUserauthV1OrganizationState | undefined) {
  const items = getUserauthOrganizationStateListItems(data);

  return [items.data.controls.settings.requireMfa];
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load organization', 'view this organization');

function OrganizationDetailLayout() {
  const { organizationId } = Route.useParams();
  const [selectedTab, setSelectedTab] = useState<DetailOverviewTab>('details');
  const { data, error, isPending } = useIxmUserauthV1OrganizationQueryServiceOrganizationGet({ organizationId });
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.organization), [data?.organization]);
  const controlListItems = useMemo(() => getControlListItems(data?.organization), [data?.organization]);
  const { activeTenantIsPlatform } = useActiveRealmAPIContext();

  const tabs: Tab<DetailOverviewTab>[] = useMemo(
    () => [
      {
        label: 'Details',
        value: 'details',
        children: <AppReadonlyList isPending={showPending} items={listItems} />,
      },
      {
        label: 'Controls',
        value: 'controls',
        children: <AppReadonlyList isPending={showPending} items={controlListItems} />,
      },
    ],
    [showPending, listItems, controlListItems],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <UserauthOrganizationStatusPill isPending={showPending} size="large" status={data?.organization?.status} />
          </HorizontalStack>

          <UserauthOrganizationEntityMenu organizationId={organizationId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <WrappingLayoutContainer
          gap={DEFAULT_PAGE_LAYOUT_GAP}
          left={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (
              <Tabs
                activeTab={selectedTab}
                fullWidth={false}
                gap={7}
                id="organizationDetails"
                label="Detail Overviews"
                onTabChange={setSelectedTab}
                tabs={tabs}
              />
            ),
          }}
          padding={LAYOUT_ZERO_CONTAINER_PADDING}
          right={{
            ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
            render: (
              <Box backgroundColor="neutral-xlt" borderRadius="8px" padding={DEFAULT_BOX_PADDING}>
                <VerticalStack gap={4}>
                  <Typography.Level3>Programs</Typography.Level3>
                  <UserauthOrganizationProgramsList organizationId={organizationId} />
                </VerticalStack>
              </Box>
            ),
          }}
          wrap="forward"
          wrapGap={7}
        />
      </PageLayout.Section>

      {activeTenantIsPlatform ? (
        <PageLayout.Section>
          <Outlet />
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
